import { useEffect, useRef } from "react";
import {
  useForm as useFormHook,
  UseFormProps as UseFormPropsHook,
  DefaultValues,
  SubmitErrorHandler,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TypeOf, ZodSchema } from "zod";
import { z } from "zod";

export interface UseFormOptions<Z extends ZodSchema>
  extends Omit<UseFormPropsHook<TypeOf<Z>>, "resolver"> {
  defaultValues?: DefaultValues<TypeOf<Z>>;
  schema: Z;
  onSubmit: (data: z.infer<Z>) => void;
  /** Called when the user submits and validation fails. Receives the
   *  errors object so callers can surface feedback (e.g., a toast). */
  onInvalid?: SubmitErrorHandler<z.infer<Z>>;
  onClose?: () => void;
}

export const useForm = <Z extends ZodSchema>({
  defaultValues,
  mode = "all",
  schema,
  onSubmit,
  onInvalid,
  onClose,
  ...formProps
}: UseFormOptions<Z>) => {
  // Track if the form has been modified by user interaction
  const hasUserInteracted = useRef(false);
  // Track the initial defaultValues to detect when real data arrives
  const initialDefaultValuesRef = useRef(defaultValues);

  const form = useFormHook<z.infer<Z>>({
    ...formProps,
    resolver: zodResolver(schema as any),
    mode,
    defaultValues: defaultValues as any,
  });

  const { reset, setError } = form;

  const handleFormSubmit = async (data: z.infer<typeof schema>) => {
    try {
      await onSubmit(data);

      // Reset dirty state after successful submit
      hasUserInteracted.current = false;
    } catch (error) {
      setError("root", {
        type: "manual",
        message:
          error instanceof Error ? error.message : "Failed to submit form",
      });
    }
  };

  // Track when user starts interacting with the form
  useEffect(() => {
    const subscription = form.watch(() => {
      hasUserInteracted.current = true;
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Reset form when defaultValues change (e.g., when data loads)
  // Only reset if user hasn't interacted with the form
  useEffect(() => {
    // Skip if this is the initial mount (defaultValues haven't changed)
    if (defaultValues === initialDefaultValuesRef.current) {
      return;
    }

    // Reset the form if the user hasn't interacted with it
    if (!hasUserInteracted.current) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form]);

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = form.handleSubmit(handleFormSubmit, onInvalid);

  return { form, handleSubmit, handleClose };
};

export default useForm;
